import { z } from "zod";
import { canonicalJson } from "./canonical.js";
import { LOCAL_CLIENT_KINDS } from "./presence.js";
import { BridgeHandshakeSchema } from "./protocol.js";
export const HandleSchema = z.string().min(1).max(48)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase ASCII slug");
export const UuidSchema = z.string().uuid();
export const IsoDateSchema = z.string().datetime({ offset: true });
export const Base64UrlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/)
    .refine(isCanonicalBase64Url, "must be canonical unpadded base64url");
export const ClientKindSchema = z.enum(["claude", "codex", "dashboard"]);
/** Clients that run on a member's machine; the dashboard session is never one of them. */
export const LocalClientKindSchema = z.enum(LOCAL_CLIENT_KINDS);
export const ExecutionModeSchema = z.enum(["interactive", "spawned_headless", "terminal_first"]);
export const MessageTypeSchema = z.enum(["handoff", "chat", "wip", "report", "activity"]);
export const ContentVisibilitySchema = z.enum(["participants", "team"]);
export const MessageStateSchema = z.enum(["queued", "opened", "acknowledged", "completed"]);
export const CompletionOutcomeSchema = z.enum(["success", "partial", "blocked", "failed"]);
export const KeyEpochSchema = z.number().int().positive();
export const CiphertextRevisionSchema = z.number().int().positive();
export const CiphertextRevisionOrZeroSchema = z.number().int().nonnegative();
export const EncryptedBlobSchema = z.object({
    formatVersion: z.literal(1),
    keyEpoch: KeyEpochSchema,
    keyId: z.string().min(1).max(80),
    nonce: Base64UrlSchema,
    ciphertext: Base64UrlSchema,
});
export const MutableEncryptedBlobSchema = EncryptedBlobSchema.extend({
    revision: CiphertextRevisionSchema,
});
export const FILE_CHUNK_BYTES = 1024 * 1024;
export const MAX_INLINE_CIPHERTEXT_BYTES = 256 * 1024;
export const MAX_ATTACHMENT_PLAINTEXT_BYTES = 100 * 1024 * 1024;
export const MAX_ATTACHMENT_CIPHERTEXT_BYTES = 101 * 1024 * 1024;
export const SecretstreamEnvelopeDescriptorSchema = z.object({
    formatVersion: z.literal(1),
    keyEpoch: KeyEpochSchema.max(0xffff_ffff),
    keyId: z.string().min(1).max(80),
    header: Base64UrlSchema,
    chunkBytes: z.literal(FILE_CHUNK_BYTES),
    chunkCount: z.number().int().positive().max(100),
});
export const AttachmentMetadataSchema = z.object({
    name: z.string().min(1).max(255),
    mediaType: z.string().min(1).max(255),
    note: z.string().max(2_000).optional(),
});
export const AttachmentStateSchema = z.enum(["uploading", "finalized", "cleanup_pending", "deleted"]);
export const CompletionResultPayloadSchema = z.object({
    outcome: CompletionOutcomeSchema,
    summary: z.string().min(1),
    commitSha: z.string().regex(/^[0-9a-f]{40}$/).optional(),
});
export const HANDOVER_LIST_MAX_ITEMS = 40;
export const HANDOVER_ITEM_MAX_CHARS = 400;
export const HANDOVER_NOTES_MAX_CHARS = 4_000;
export const HANDOVER_MAX_REPOSITORIES = 8;
export const MAX_HANDOVER_DOCUMENT_BYTES = 32 * 1024;
/** `metadata.via` value recorded inside the ciphertext when a message was sent from the Telegram relay. */
export const MESSAGE_VIA_TELEGRAM = "telegram";
/** A checkout the handover refers to; `owner`/`name` mirror the GitHub mirror, `commit` is a full sha. */
export const HandoverRepositorySchema = z.object({
    repositoryId: UuidSchema.optional(),
    owner: z.string().min(1).max(255).optional(),
    name: z.string().min(1).max(255),
    branch: z.string().min(1).max(255).optional(),
    commit: z.string().regex(/^[0-9a-f]{40}$/).optional(),
    dirty: z.boolean().optional(),
});
const HandoverListSchema = z.array(z.string().trim().min(1).max(HANDOVER_ITEM_MAX_CHARS))
    .max(HANDOVER_LIST_MAX_ITEMS).default([]);
/**
 * Structured block an encrypted `handoff` may carry: what was done, what is left, how to
 * verify it and where the work lives. Lives only inside the ciphertext. It is a ZodEffects
 * (byte cap), so never `.pick/.extend` it or hand it to the bridge's `phased()`.
 */
export const HandoverDocumentSchema = z.object({
    version: z.literal(1),
    project: z.object({ id: UuidSchema, name: z.string().trim().min(1).max(120) }).optional(),
    repositories: z.array(HandoverRepositorySchema).max(HANDOVER_MAX_REPOSITORIES).default([]),
    done: HandoverListSchema,
    todo: HandoverListSchema,
    verify: HandoverListSchema,
    ideas: HandoverListSchema,
    notes: z.string().trim().min(1).max(HANDOVER_NOTES_MAX_CHARS).optional(),
}).superRefine((value, context) => {
    if (new TextEncoder().encode(canonicalJson(value)).byteLength > MAX_HANDOVER_DOCUMENT_BYTES) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "handover document exceeds 32 KiB" });
    }
});
export const EncryptedMessagePayloadSchema = z.object({
    subject: z.string().max(240).optional(),
    body: z.string().min(1),
    acceptanceCriteria: z.array(z.string().min(1).max(2_000)).max(50).optional(),
    completion: CompletionResultPayloadSchema.optional(),
    metadata: z.record(z.string(), z.string()).optional(),
    /** Only meaningful on `handoff` messages; senders enforce that, the schema stays type-agnostic. */
    handover: HandoverDocumentSchema.optional(),
});
export const MessageEnvelopeSchema = z.object({
    id: UuidSchema,
    senderAgentId: UuidSchema,
    recipientAgentIds: z.array(UuidSchema).min(1).max(32),
    type: MessageTypeSchema,
    visibility: ContentVisibilitySchema,
    ciphertext: EncryptedBlobSchema,
    attachmentIds: z.array(UuidSchema).max(32),
    replyToMessageId: UuidSchema.nullable().default(null),
    repoId: UuidSchema.nullable().default(null),
    projectId: UuidSchema.nullable().default(null),
    expiresAt: IsoDateSchema.nullable().default(null),
    createdAt: IsoDateSchema,
});
export const MessageRecipientStateSchema = z.object({
    agentId: UuidSchema,
    state: MessageStateSchema,
    resultCiphertext: EncryptedBlobSchema.nullable(),
    openedAt: IsoDateSchema.nullable(),
    acknowledgedAt: IsoDateSchema.nullable(),
    completedAt: IsoDateSchema.nullable(),
});
export const MessageViewSchema = MessageEnvelopeSchema.extend({
    recipientStates: z.array(MessageRecipientStateSchema),
});
export const CreateMessageSchema = MessageEnvelopeSchema.pick({
    id: true,
    recipientAgentIds: true,
    type: true,
    visibility: true,
    ciphertext: true,
    attachmentIds: true,
    replyToMessageId: true,
    repoId: true,
    projectId: true,
    expiresAt: true,
}).extend({
    attachmentIds: z.array(UuidSchema).max(32).default([]),
    idempotencyKey: z.string().min(16).max(160),
});
/** Routing-only payload carried by message.* outbox events; never contains ciphertext or content. */
export const MessageRoutingEventSchema = z.object({
    messageId: UuidSchema,
    type: MessageTypeSchema,
    visibility: ContentVisibilitySchema,
    projectId: UuidSchema.nullable(),
    repoId: UuidSchema.nullable(),
    senderAgentId: UuidSchema,
    recipientAgentIds: z.array(UuidSchema).min(1).max(32),
    agentId: UuidSchema.optional(),
    state: MessageStateSchema.optional(),
    at: IsoDateSchema.optional(),
});
export const CreateMessageBatchSchema = z.object({
    items: z.array(CreateMessageSchema).min(1).max(4),
    idempotencyKey: z.string().min(16).max(160),
});
export const AttachmentViewSchema = z.object({
    id: UuidSchema,
    messageId: UuidSchema.nullable(),
    metadataCiphertext: EncryptedBlobSchema,
    contentEnvelope: SecretstreamEnvelopeDescriptorSchema,
    ciphertextSizeBytes: z.number().int().positive().max(MAX_ATTACHMENT_CIPHERTEXT_BYTES),
    ciphertextSha256: z.string().regex(/^[0-9a-f]{64}$/),
    state: AttachmentStateSchema,
    createdAt: IsoDateSchema,
    expiresAt: IsoDateSchema,
    finalizedAt: IsoDateSchema.nullable(),
});
export const CreateAttachmentSchema = AttachmentViewSchema.pick({
    id: true,
    metadataCiphertext: true,
    contentEnvelope: true,
    ciphertextSizeBytes: true,
    ciphertextSha256: true,
}).extend({ idempotencyKey: z.string().min(16).max(160) });
export const FinalizeAttachmentSchema = z.object({ idempotencyKey: z.string().min(16).max(160) });
export const TransitionMessageSchema = z.object({
    idempotencyKey: z.string().min(16).max(160),
});
export const CompleteMessageSchema = TransitionMessageSchema.extend({
    resultCiphertext: EncryptedBlobSchema,
});
export const MessageListQuerySchema = z.object({
    types: z.array(MessageTypeSchema).max(5).optional(),
    projectId: UuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: IsoDateSchema.optional(),
});
export const AgentSchema = z.object({
    id: UuidSchema,
    ownerUserId: UuidSchema,
    handle: HandleSchema,
    role: HandleSchema.nullable(),
    icon: z.string().max(16),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    retiredAt: IsoDateSchema.nullable(),
    /** Owner's handle; present on team-scoped listings so clients can print @owner/agent without a second lookup. */
    ownerHandle: HandleSchema.optional(),
    /** Encrypted listening note (field `agent.listening_note`); optional so agents from older servers still parse. */
    listeningNote: MutableEncryptedBlobSchema.nullable().optional(),
    /** Row revision of the listening note (0 when never set). Clients write revision + 1; it keeps counting after a clear left the envelope null. */
    listeningNoteRevision: z.number().int().min(0).optional(),
});
export const AddressCandidateSchema = AgentSchema.pick({ id: true, handle: true, role: true });
export const AddressResolutionSchema = z.discriminatedUnion("ok", [
    z.object({ ok: z.literal(true), agent: AddressCandidateSchema }),
    z.object({
        ok: z.literal(false),
        reason: z.enum(["not_found", "ambiguous"]),
        candidates: z.array(AddressCandidateSchema),
    }),
]);
export const ResolveAddressQuerySchema = z.object({ address: z.string().min(1).max(160) });
/** Mutable-field domain of an agent's listening note (the `field` in `mutableFieldAad`). */
export const LISTENING_NOTE_FIELD = "agent.listening_note";
export const LISTENING_NOTE_MAX_CHARS = 500;
/** Plaintext of a listening note; the Telegram relay answers with it when the agent is offline. */
export const ListeningNotePayloadSchema = z.object({
    text: z.string().trim().min(1).max(LISTENING_NOTE_MAX_CHARS),
});
/**
 * Same rule as the DB's `*_revision_check`: a null ciphertext clears the note at any
 * revision >= 0; a present envelope must carry exactly the row revision (> 0).
 */
export const UpdateListeningNoteSchema = z.object({
    ciphertext: MutableEncryptedBlobSchema.nullable(),
    revision: CiphertextRevisionOrZeroSchema,
    idempotencyKey: z.string().min(16).max(160),
}).superRefine((value, context) => {
    if (value.ciphertext !== null && value.ciphertext.revision !== value.revision) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "listening note envelope and row revisions must match" });
    }
});
export const DeviceSchema = z.object({
    id: z.string().min(16).max(160),
    encryptedLabel: MutableEncryptedBlobSchema.nullable(),
    labelRevision: CiphertextRevisionOrZeroSchema,
    lastSeenAt: IsoDateSchema,
});
export const DeviceViewSchema = DeviceSchema.extend({
    userId: UuidSchema,
    createdAt: IsoDateSchema,
});
export const InstallationReadinessSchema = z.enum([
    "unknown", "ready", "needs_setup", "incompatible", "terminal_first",
]);
export const AgentInstallationSchema = z.object({
    agentId: UuidSchema,
    deviceId: z.string().min(16).max(160),
    client: LocalClientKindSchema,
    readiness: InstallationReadinessSchema,
    preferredProfileKind: z.enum(["inherit", "project", "user"]),
    encryptedProfileSummary: MutableEncryptedBlobSchema.nullable(),
    profileSummaryRevision: CiphertextRevisionOrZeroSchema,
    configDigest: z.string().max(128).nullable(),
    verifiedAt: IsoDateSchema.nullable(),
});
export const ReportAgentInstallationSchema = AgentInstallationSchema.extend({
    idempotencyKey: z.string().min(16).max(160),
});
export const IntentStateSchema = z.enum(["queued", "claimed", "completed", "expired", "failed"]);
export const RuntimeIntentSchema = z.object({
    id: UuidSchema,
    userId: UuidSchema,
    deviceId: z.string().min(16).max(160).nullable(),
    agentId: UuidSchema.nullable(),
    kind: z.enum(["initialize_agent", "reconfigure_agent", "install_skill", "publish_skill"]),
    state: IntentStateSchema,
    payloadCiphertext: EncryptedBlobSchema,
    expiresAt: IsoDateSchema,
    createdAt: IsoDateSchema,
});
export const CreateRuntimeIntentSchema = RuntimeIntentSchema.pick({
    id: true,
    deviceId: true,
    agentId: true,
    kind: true,
    payloadCiphertext: true,
    expiresAt: true,
}).extend({ idempotencyKey: z.string().min(16).max(160) });
export const TransitionRuntimeIntentSchema = z.object({
    target: z.enum(["claimed", "completed", "failed"]),
    idempotencyKey: z.string().min(16).max(160),
});
export const AgentSetupIntentPayloadSchema = z.object({
    client: LocalClientKindSchema,
    preferredProfileKind: z.literal("inherit"),
    requestedAction: z.enum(["setup", "repair"]),
});
export const SkillInstallIntentPayloadSchema = z.object({
    skillId: UuidSchema,
    version: z.number().int().positive(),
    client: LocalClientKindSchema,
    scope: z.enum(["user", "repository"]),
    repositoryId: UuidSchema.optional(),
});
export const SessionSchema = z.object({
    id: UuidSchema,
    deviceId: z.string().min(16).max(160),
    userId: UuidSchema,
    boundAgentId: UuidSchema.nullable(),
    parentSessionId: UuidSchema.nullable(),
    resumedFromSessionId: UuidSchema.nullable(),
    lineageRootSessionId: UuidSchema,
    client: ClientKindSchema,
    executionMode: ExecutionModeSchema,
    encryptedLabel: MutableEncryptedBlobSchema.nullable(),
    labelRevision: CiphertextRevisionOrZeroSchema,
    bridgeProtocolVersion: z.number().int().positive(),
    bridgeVersion: z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/),
    bridgeDistributionChannel: z.literal("npm"),
    heartbeatAt: IsoDateSchema,
    createdAt: IsoDateSchema,
});
export const RegisterSessionSchema = SessionSchema.pick({
    id: true, deviceId: true, parentSessionId: true, resumedFromSessionId: true,
    client: true, executionMode: true, encryptedLabel: true,
    labelRevision: true,
}).extend({ labelRevision: CiphertextRevisionOrZeroSchema.default(0) });
export const SessionHandshakeSchema = z.object({
    session: RegisterSessionSchema,
    bridge: BridgeHandshakeSchema,
});
export const BindSessionSchema = z.object({
    agentId: UuidSchema,
    idempotencyKey: z.string().min(16).max(160),
    spawnDescriptorId: UuidSchema.optional(),
});
export const HeartbeatSessionSchema = z.object({
    idempotencyKey: z.string().min(16).max(160),
});
export const CurrentUserSchema = z.object({
    id: UuidSchema,
    handle: HandleSchema,
    generalAgentId: UuidSchema.nullable(),
});
/** The server-managed session the dashboard sends chat from; bound to the user's general agent. */
export const DashboardSessionSchema = z.object({
    session: SessionSchema,
    generalAgent: AgentSchema,
});
export const AwaitQuerySchema = z.object({
    cursor: z.string().optional(),
    types: z.array(MessageTypeSchema).max(5).optional(),
    timeoutSeconds: z.coerce.number().int().min(0).max(45).default(45),
});
export const AwaitResponseSchema = z.object({
    messages: z.array(MessageViewSchema),
    cursor: z.string(),
    deadlineToken: z.string(),
});
export const CreateTokenSchema = z.object({
    id: UuidSchema,
    idempotencyKey: z.string().min(16).max(160),
    tokenDigest: z.string().regex(/^[0-9a-f]{64}$/),
    encryptedLabel: MutableEncryptedBlobSchema.nullable().default(null),
    labelRevision: CiphertextRevisionOrZeroSchema.default(0),
    expiresAt: IsoDateSchema.nullable().default(null),
});
export const TokenCreatedSchema = z.object({
    id: UuidSchema,
    tokenDigest: z.string().regex(/^[0-9a-f]{64}$/),
    createdAt: IsoDateSchema,
    expiresAt: IsoDateSchema.nullable(),
});
export const AuthTokenViewSchema = TokenCreatedSchema.extend({
    encryptedLabel: MutableEncryptedBlobSchema.nullable(),
    labelRevision: CiphertextRevisionOrZeroSchema,
    revokedAt: IsoDateSchema.nullable(),
});
export const RevokeTokenSchema = z.object({ idempotencyKey: z.string().min(16).max(160) });
/**
 * CLI pairing. `baton login` generates its own token and posts only the digest, exactly as
 * the GitHub exchange does, then shows the short code the response returns once; a signed-in
 * browser approves that code and the API mints the token. The alphabet drops the glyphs that
 * are misread or misheard (0/O, 1/I/L), and the code is rendered XXXX-XXXX for legibility.
 */
export const CLI_PAIRING_CODE_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
export const CLI_PAIRING_CODE_LENGTH = 8;
const cliPairingCodePattern = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{8}$/;
const cliPairingDisplayPattern = /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{4}-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{4}$/;
/** Uppercase, dash and whitespace removed, so a typed code and a pasted one digest alike. */
export function normalizeCliPairingCode(raw) {
    return raw.replace(/[\s-]+/g, "").toUpperCase();
}
/** XXXX-XXXX: the only form a human is ever shown. */
export function formatCliPairingCode(raw) {
    const normalized = normalizeCliPairingCode(raw);
    return `${normalized.slice(0, 4)}-${normalized.slice(4)}`;
}
/** Accepts either form and yields the normalized code that both stores digest. */
export const CliPairingUserCodeSchema = z.string().min(CLI_PAIRING_CODE_LENGTH).max(24)
    .transform(normalizeCliPairingCode)
    .refine((code) => cliPairingCodePattern.test(code), "must be eight characters of the Baton pairing alphabet");
/** `expired` also covers a code already spent: an approval is reported exactly once. */
export const CliPairingStateSchema = z.enum(["pending", "approved", "denied", "expired", "unknown"]);
export const CreateCliPairingSchema = z.object({
    tokenId: UuidSchema,
    tokenDigest: z.string().regex(/^[0-9a-f]{64}$/),
    deviceId: z.string().min(1).max(255),
    // Plaintext routing metadata by design: the approver must recognise the machine.
    label: z.string().min(1).max(80),
});
export const CliPairingCreatedSchema = z.object({
    userCode: z.string().regex(cliPairingDisplayPattern),
    expiresAt: IsoDateSchema,
    verificationUrl: z.string().url().nullable(),
});
export const CliPairingStatusSchema = z.object({
    status: CliPairingStateSchema,
    user: z.object({ id: UuidSchema, handle: HandleSchema }).optional(),
});
export const CliPairingViewSchema = z.object({
    label: z.string().max(80),
    deviceId: z.string().max(255),
    expiresAt: IsoDateSchema,
    status: CliPairingStateSchema,
});
/** The body of every code-addressed pairing route: status, approve, and deny. */
export const ApproveCliPairingSchema = z.object({ userCode: CliPairingUserCodeSchema });
export const CreateAgentSchema = z.object({
    handle: HandleSchema,
    role: HandleSchema,
    icon: z.string().max(16).default("agent"),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#5f6fff"),
    idempotencyKey: z.string().min(16).max(160),
});
export const RetireAgentSchema = z.object({
    fallbackAgentId: UuidSchema,
    idempotencyKey: z.string().min(16).max(160),
});
export const RepositoryViewSchema = z.object({
    id: UuidSchema,
    githubId: z.number().int().nonnegative(),
    installationId: z.number().int().nonnegative(),
    ownerHandle: z.string().min(1).max(255),
    name: z.string().min(1).max(255),
    private: z.boolean(),
    archived: z.boolean(),
    defaultBranch: z.string().min(1).max(255),
    permission: z.string().min(1).max(32),
    lastReconciledAt: IsoDateSchema,
});
export const ProjectMemberRoleSchema = z.enum(["owner", "admin", "member"]);
export const ProjectMemberStateSchema = z.enum(["invited", "active", "revoked"]);
export const ProjectNamePayloadSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
export const ProjectSummarySchema = z.object({
    id: UuidSchema,
    ownerUserId: UuidSchema,
    nameCiphertext: MutableEncryptedBlobSchema,
    nameRevision: CiphertextRevisionSchema,
    role: ProjectMemberRoleSchema,
    visibleRepositoryCount: z.number().int().nonnegative(),
    activeMemberCount: z.number().int().positive(),
    createdAt: IsoDateSchema,
    archivedAt: IsoDateSchema.nullable(),
});
export const ProjectMemberViewSchema = z.object({
    userId: UuidSchema,
    handle: HandleSchema,
    role: ProjectMemberRoleSchema,
    state: ProjectMemberStateSchema,
    invitedByUserId: UuidSchema,
    invitedAt: IsoDateSchema,
    acceptedAt: IsoDateSchema.nullable(),
});
export const ProjectAgentAssignmentSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    repositoryId: UuidSchema.nullable(),
    agent: AgentSchema,
    assignedByUserId: UuidSchema,
    createdAt: IsoDateSchema,
});
/** Canvas node ids are routing-level: `project:<id>`, `repository:<id>`, `agent:<agentId>`. */
export const CanvasNodeIdSchema = z.string().min(3).max(96)
    .regex(/^(project|repository|agent):[A-Za-z0-9-]{1,80}$/);
export const CanvasPositionSchema = z.object({
    nodeId: CanvasNodeIdSchema,
    x: z.number().int().min(-100_000).max(100_000),
    y: z.number().int().min(-100_000).max(100_000),
    updatedByUserId: UuidSchema,
    updatedAt: IsoDateSchema,
});
export const UpsertCanvasPositionsSchema = z.object({
    positions: z.array(CanvasPositionSchema.pick({ nodeId: true, x: true, y: true })).min(1).max(200),
    idempotencyKey: z.string().min(16).max(160),
}).superRefine((value, context) => {
    if (new Set(value.positions.map((position) => position.nodeId)).size !== value.positions.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "node IDs must be unique" });
    }
});
/** A conversation wire between two agents in a project: routing metadata only, never content. */
export const ProjectWireSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    sourceAgentId: UuidSchema,
    targetAgentId: UuidSchema,
    createdByUserId: UuidSchema,
    createdAt: IsoDateSchema,
});
export const CreateProjectWireSchema = z.object({
    id: UuidSchema,
    sourceAgentId: UuidSchema,
    targetAgentId: UuidSchema,
    idempotencyKey: z.string().min(16).max(160),
}).refine((value) => value.sourceAgentId !== value.targetAgentId, {
    message: "a wire needs two different agents",
});
export const ProjectCanvasSchema = z.object({
    positions: z.array(CanvasPositionSchema),
    wires: z.array(ProjectWireSchema),
});
export const ProjectGraphSchema = z.object({
    project: ProjectSummarySchema,
    repositories: z.array(RepositoryViewSchema),
    members: z.array(ProjectMemberViewSchema),
    agentAssignments: z.array(ProjectAgentAssignmentSchema),
    canvas: ProjectCanvasSchema,
});
export const ProjectInvitationSchema = z.object({
    projectId: UuidSchema,
    ownerUserId: UuidSchema,
    nameCiphertext: MutableEncryptedBlobSchema,
    nameRevision: CiphertextRevisionSchema,
    role: z.enum(["admin", "member"]),
    invitedByUserId: UuidSchema,
    invitedByHandle: HandleSchema,
    invitedAt: IsoDateSchema,
    accessibleRepositoryCount: z.number().int().nonnegative(),
});
/**
 * Everyone who could join a project, as any active member may see them. GitHub mirroring
 * creates a user row for every collaborator it finds, so this list also holds people who
 * have never opened Baton: `signedIn` is false for them and the UI must say so.
 */
export const EligibleProjectCollaboratorSchema = z.object({
    userId: UuidSchema,
    handle: HandleSchema,
    githubLogin: z.string().max(255).nullable(),
    signedIn: z.boolean(),
    repositoryIds: z.array(UuidSchema).min(1),
    membership: ProjectMemberViewSchema.pick({ role: true, state: true }).nullable(),
});
export const CreateProjectSchema = z.object({
    id: UuidSchema,
    nameCiphertext: MutableEncryptedBlobSchema,
    nameRevision: CiphertextRevisionSchema,
    repositoryIds: z.array(UuidSchema).max(32).default([]),
    idempotencyKey: z.string().min(16).max(160),
}).superRefine((value, context) => {
    if (value.nameCiphertext.revision !== value.nameRevision) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "project name envelope and row revisions must match" });
    }
    if (new Set(value.repositoryIds).size !== value.repositoryIds.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "repository IDs must be unique" });
    }
});
export const UpdateProjectNameSchema = z.object({
    nameCiphertext: MutableEncryptedBlobSchema,
    nameRevision: CiphertextRevisionSchema,
    idempotencyKey: z.string().min(16).max(160),
}).superRefine((value, context) => {
    if (value.nameCiphertext.revision !== value.nameRevision) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "project name envelope and row revisions must match" });
    }
});
export const AddProjectRepositorySchema = z.object({
    repositoryId: UuidSchema,
    idempotencyKey: z.string().min(16).max(160),
});
export const InviteProjectMemberSchema = z.object({
    userId: UuidSchema,
    role: z.enum(["admin", "member"]).default("member"),
    idempotencyKey: z.string().min(16).max(160),
});
export const UpdateProjectMemberRoleSchema = z.object({
    role: z.enum(["admin", "member"]),
    idempotencyKey: z.string().min(16).max(160),
});
export const AcceptProjectInvitationSchema = z.object({
    idempotencyKey: z.string().min(16).max(160),
});
/** Raw invite token: 32 random bytes as unpadded base64url. Only its digest is stored. */
export const InviteLinkTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const ProjectInviteLinkSchema = z.object({
    id: UuidSchema,
    projectId: UuidSchema,
    role: z.enum(["admin", "member"]),
    createdByUserId: UuidSchema,
    expiresAt: IsoDateSchema.nullable(),
    maxUses: z.number().int().positive().nullable(),
    useCount: z.number().int().nonnegative(),
    revokedAt: IsoDateSchema.nullable(),
    createdAt: IsoDateSchema,
});
export const ProjectInviteLinkCreatedSchema = ProjectInviteLinkSchema.extend({
    token: InviteLinkTokenSchema,
    url: z.string().url().nullable(),
});
export const CreateProjectInviteLinkSchema = z.object({
    id: UuidSchema,
    role: z.enum(["admin", "member"]).default("member"),
    expiresAt: IsoDateSchema.nullable().default(null),
    maxUses: z.number().int().min(1).max(1_000).nullable().default(null),
    idempotencyKey: z.string().min(16).max(160),
});
export const ProjectInviteLinkPreviewSchema = z.object({
    projectId: UuidSchema,
    ownerUserId: UuidSchema,
    nameCiphertext: MutableEncryptedBlobSchema,
    nameRevision: CiphertextRevisionSchema,
    role: z.enum(["admin", "member"]),
    invitedByUserId: UuidSchema,
    invitedByHandle: HandleSchema,
    expiresAt: IsoDateSchema.nullable(),
    activeMemberCount: z.number().int().positive(),
    membership: ProjectMemberViewSchema.pick({ role: true, state: true }).nullable(),
});
export const AcceptProjectInviteLinkSchema = z.object({
    idempotencyKey: z.string().min(16).max(160),
});
export const AssignProjectAgentSchema = z.object({
    id: UuidSchema,
    agentId: UuidSchema,
    repositoryId: UuidSchema.nullable().default(null),
    idempotencyKey: z.string().min(16).max(160),
});
export const ProjectMutationSchema = z.object({
    idempotencyKey: z.string().min(16).max(160),
});
export const GitHubProfileSchema = z.object({
    id: z.number().int().nonnegative(),
    login: z.string().min(1).max(255),
});
/** Telegram relay state for one user; `available` is false when the API runs without the relay. */
export const TelegramStatusSchema = z.object({
    available: z.boolean(),
    botUsername: z.string().nullable(),
    linked: z.boolean(),
    linkedAt: IsoDateSchema.nullable(),
});
/** `acknowledged` is the consent click: the relay decrypts to and from your agents while forwarding. */
export const CreateTelegramLinkSchema = z.object({
    acknowledged: z.literal(true),
    idempotencyKey: z.string().min(16).max(160),
});
/** Single-use deep link (`https://t.me/<bot>?start=<code>`); only the code's digest is stored. */
export const TelegramLinkCreatedSchema = z.object({
    url: z.string().url(),
    expiresAt: IsoDateSchema,
});
export const TEAM_BRIEFING_ID = "00000000-0000-4000-8000-00000000b001";
export const BriefingContentSchema = z.object({ text: z.string().min(1).max(20_000) });
export const TeamBriefingSchema = z.object({
    id: z.literal(TEAM_BRIEFING_ID),
    contentCiphertext: MutableEncryptedBlobSchema,
    contentRevision: CiphertextRevisionSchema,
    mac: Base64UrlSchema.refine((value) => value.length === 43, "briefing MAC must be 32 bytes"),
    updatedByUserId: UuidSchema,
    updatedAt: IsoDateSchema,
});
export const UpdateTeamBriefingSchema = TeamBriefingSchema.pick({
    contentCiphertext: true,
    contentRevision: true,
    mac: true,
}).extend({ idempotencyKey: z.string().min(16).max(160) });
export const EventStreamTicketRequestSchema = z.object({
    afterId: z.number().int().nonnegative().default(0),
});
export const EventStreamTicketSchema = z.object({
    ticket: z.string().min(32),
    expiresAt: IsoDateSchema,
    websocketPath: z.literal("/v1/events/ws"),
    websocketProtocol: z.string().startsWith("baton-ticket-v1."),
});
export const OutboxEventSchema = z.object({
    id: z.number().int().nonnegative(),
    topic: z.string().min(1).max(80),
    aggregateId: UuidSchema,
    payload: z.record(z.string(), z.unknown()),
    createdAt: IsoDateSchema,
});
export const IntentKindSchema = z.enum([
    "initialize_agent", "reconfigure_agent", "install_skill", "publish_skill",
]);
export const SkillInventoryScopeSchema = z.enum(["user", "repository"]);
export const SkillInventoryScanSchema = z.object({
    deviceId: z.string().min(16).max(160),
    client: LocalClientKindSchema,
    scope: SkillInventoryScopeSchema,
    scopeKey: z.string().min(1).max(160),
    repositoryId: UuidSchema.nullable(),
    inventoryDigest: z.string().regex(/^[0-9a-f]{64}$/),
    scannedAt: IsoDateSchema,
}).superRefine((value, context) => {
    if (value.scope === "user" && (value.scopeKey !== "user" || value.repositoryId !== null)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "user scans use scopeKey=user and no repository" });
    }
    if (value.scope === "repository" && (value.repositoryId === null || value.scopeKey !== value.repositoryId)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "repository scans key by repository id" });
    }
});
export const RecordSkillInventoryScanSchema = SkillInventoryScanSchema.and(z.object({
    idempotencyKey: z.string().min(16).max(160),
}));
export const SkillVisibilitySchema = z.enum(["private", "team", "public_pointer"]);
export const SkillFileSchema = z.object({
    path: z.string().min(1).max(240)
        .regex(/^(?!\/)(?!.*(?:^|\/)\.\.(?:\/|$))(?!.*\\)[A-Za-z0-9._/-]+$/),
    content: z.string().max(256 * 1024),
});
export const SkillBundleSchema = z.object({
    files: z.array(SkillFileSchema).min(1).max(100),
}).superRefine((value, context) => {
    const paths = new Set();
    let bytes = 0;
    for (const file of value.files) {
        if (paths.has(file.path))
            context.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate skill path: ${file.path}` });
        paths.add(file.path);
        bytes += new TextEncoder().encode(file.content).byteLength;
    }
    if (!paths.has("SKILL.md"))
        context.addIssue({ code: z.ZodIssueCode.custom, message: "skill bundle requires SKILL.md" });
    if (bytes > 200 * 1024)
        context.addIssue({ code: z.ZodIssueCode.custom, message: "skill text exceeds 200 KiB" });
});
export const SkillProvenanceSchema = z.object({
    source: z.enum(["local", "team", "github"]),
    sourceReference: z.string().max(2_000).optional(),
    sourceSha: z.string().regex(/^[0-9a-f]{40}$/).optional(),
});
export const SkillSummarySchema = z.object({
    id: UuidSchema,
    ownerUserId: UuidSchema,
    slug: z.string().min(1).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    visibility: SkillVisibilitySchema,
    latestVersion: z.number().int().positive(),
    createdAt: IsoDateSchema,
});
export const SkillVersionViewSchema = z.object({
    id: UuidSchema,
    skillId: UuidSchema,
    version: z.number().int().positive(),
    contentCiphertext: EncryptedBlobSchema,
    provenanceCiphertext: EncryptedBlobSchema.nullable(),
    publishedAt: IsoDateSchema,
});
export const PublishSkillSchema = z.object({
    skillId: UuidSchema,
    versionId: UuidSchema,
    slug: z.string().min(1).max(80).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    visibility: SkillVisibilitySchema,
    version: z.number().int().positive(),
    contentCiphertext: EncryptedBlobSchema,
    provenanceCiphertext: EncryptedBlobSchema.nullable(),
    accessUserIds: z.array(UuidSchema).max(32).default([]),
    idempotencyKey: z.string().min(16).max(160),
});
export const RateLimitClassSchema = z.enum(["ordinary", "lineage_progress", "lineage_completion"]);
export const MutationPhaseSchema = z.enum(["prepare", "commit"]);
function isCanonicalBase64Url(value) {
    const remainder = value.length % 4;
    if (remainder === 1)
        return false;
    if (remainder === 0)
        return true;
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
    const final = alphabet.indexOf(value.at(-1));
    return remainder === 2 ? (final & 0x0f) === 0 : (final & 0x03) === 0;
}
export const ApprovalReceiptSchema = z.object({
    token: z.string().min(32),
    expiresAt: IsoDateSchema,
    previewHash: z.string().regex(/^[0-9a-f]{64}$/),
});
export const WorkspaceModeSchema = z.enum(["isolated", "worktree", "cwd", "custom"]);
export const SpawnItemSchema = z.object({
    handoffId: UuidSchema.optional(),
    draft: z.object({
        recipient: z.string().min(1),
        body: z.string().min(1),
        acceptanceCriteria: z.array(z.string().min(1)).min(1),
    }).optional(),
    client: LocalClientKindSchema,
    workspace: WorkspaceModeSchema.default("isolated"),
    customPath: z.string().optional(),
    baseSha: z.string().regex(/^[0-9a-f]{40}$/),
    timeoutMinutes: z.number().int().min(1).max(1_440).default(30),
}).refine((value) => Boolean(value.handoffId) !== Boolean(value.draft), {
    message: "provide exactly one of handoffId or draft",
});
export const LaunchSpecSchema = z.object({
    runId: UuidSchema,
    descriptorId: UuidSchema,
    client: LocalClientKindSchema,
    cwd: z.string(),
    executable: z.string(),
    argv: z.array(z.string()),
    displayCommand: z.string(),
    terminalFallback: z.boolean(),
});
//# sourceMappingURL=schemas.js.map